import { type PayloadAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import type { DateOverride } from "../helpers/date-override"
import { createPanelSlice, type PanelRootState } from "./create-panel-slice"
import { makeDuplicateFlowReducers, makeTemplateChartReducers, type TemplateChartSettings } from "./template-chart-settings"

export interface GraphState {
  selectedTemplate: string
  disabledItems: Record<string, boolean>
  // Per-template chart category, keyed by template name (factor-sensitivity splits
  // "factor-sensitivity-{predicted|historical}"); defaults live at the consumer call site.
  templateCharts: Partial<Record<string, TemplateChartSettings>>
  // Per-panel date range that supersedes the dashboard's dates when present.
  dateOverride: DateOverride | null
  isPreferencesLoaded: boolean
  /** Transient (never persisted): set by clonePanelState so the duplicate's sync hook
   *  persists the copied state once, then cleared via clearPendingClonePersist. */
  pendingClonePersist?: boolean
  showMetricsSelector: boolean
  duplicateFromViewName: string | null
  duplicateFromTemplateId: string | null
  duplicateFromViewMetrics: string[] | null
  duplicateReturnViewId: string | null
}

/** Keyed by dashboardId at the top, panelId at the inner level. */
export type GraphRootState = PanelRootState<GraphState>

export const DEFAULT_GRAPH_STATE: GraphState = {
  selectedTemplate: "performance",
  disabledItems: { residual: true },
  templateCharts: {},
  dateOverride: null,
  isPreferencesLoaded: false,
  showMetricsSelector: false,
  duplicateFromViewName: null,
  duplicateFromTemplateId: null,
  duplicateFromViewMetrics: null,
  duplicateReturnViewId: null,
}

// Preference subtype base; the sync hook and data hook both key off this.
export const GRAPH_PREFERENCE_BASE = "graph"

/** Persisted fields — single source for the setPreferences whitelist (here) and the sync hook's diff baseline. */
export const GRAPH_PREFERENCE_FIELDS = ["selectedTemplate", "disabledItems", "templateCharts", "dateOverride"] as const

/** Co-located access to a panel's raw slot (undefined when absent); callers coalesce to DEFAULT_GRAPH_STATE. */
export const selectGraphPanel = (
  state: ReduxState,
  dashboardId: string,
  panelKey: string,
): GraphState | undefined => state.dashboard.graph[dashboardId]?.[panelKey]

// Memoized per source panel object so repeat calls for unchanged state return a stable reference.
const syncOverrideCache = new WeakMap<GraphState, GraphState>()

/** Sync-only view of a panel's slot: substitutes a mid-duplicate-flow "custom" selectedTemplate
 *  with duplicateReturnViewId, so it's never diffed/persisted on its own. */
export const selectGraphPanelForSync = (
  state: ReduxState,
  dashboardId: string,
  panelKey: string,
): GraphState | undefined => {
  const panel = selectGraphPanel(state, dashboardId, panelKey)
  if (!panel || !panel.duplicateReturnViewId || panel.selectedTemplate !== "custom") {
    return panel
  }
  const cached = syncOverrideCache.get(panel)
  if (cached) return cached
  const overridden: GraphState = { ...panel, selectedTemplate: panel.duplicateReturnViewId }
  syncOverrideCache.set(panel, overridden)
  return overridden
}

export const graphSlice = createPanelSlice({
  name: "dashboard/graph",
  defaultState: DEFAULT_GRAPH_STATE,
  preferenceFields: GRAPH_PREFERENCE_FIELDS,
  // Clone disabledItems / templateCharts so dashboards don't share the default object reference.
  freshOverrides: (defaultState) => ({ disabledItems: { ...defaultState.disabledItems }, templateCharts: {} }),
  cloneOverrides: (sourceState) => ({
    disabledItems: { ...sourceState.disabledItems },
    templateCharts: { ...sourceState.templateCharts },
    showMetricsSelector: false,
    duplicateFromViewName: null,
    duplicateFromTemplateId: null,
    duplicateFromViewMetrics: null,
    duplicateReturnViewId: null,
  }),
  reducers: ({ ensure, initInstance }) => ({
    ...makeTemplateChartReducers<GraphState>(ensure),
    ...makeDuplicateFlowReducers<GraphState>(ensure),
    setSelectedTemplate(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, template: string }>,
    ) {
      const { dashboardId, panelId, template } = action.payload
      ensure(state, dashboardId, panelId).selectedTemplate = template
    },

    // Seed the module-config template as the panel baseline. No-ops once the instance exists,
    // so a saved preference that loads first is never overwritten (mirrors the pre-RTK reducer
    // initialState: config template was the initial value, saved prefs overrode it).
    initFromModuleConfig(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, selectedTemplate?: string, dateOverride?: DateOverride | null }>,
    ) {
      const { dashboardId, panelId, selectedTemplate, dateOverride } = action.payload
      const overrides: Partial<GraphState> = {}
      if (selectedTemplate != null) overrides.selectedTemplate = selectedTemplate
      if (dateOverride !== undefined) overrides.dateOverride = dateOverride
      initInstance(state, dashboardId, panelId, overrides)
    },

    setDisabledItems(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, itemId: string, defaultDisabled?: boolean }>,
    ) {
      const { dashboardId, panelId, itemId, defaultDisabled } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      const currentState = instance.disabledItems[itemId]
      instance.disabledItems[itemId] = currentState === undefined ? !defaultDisabled : !currentState
    },

    setOnlyItem(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, itemId: string, allItemIds: string[] }>,
    ) {
      const { dashboardId, panelId, itemId, allItemIds } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      instance.disabledItems = Object.fromEntries(
        allItemIds.map((id) => [id, id !== itemId]),
      )
    },

    enableItems(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, itemIds: string[] }>,
    ) {
      const { dashboardId, panelId, itemIds } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      Object.assign(
        instance.disabledItems,
        Object.fromEntries(itemIds.map((id) => [id, false])),
      )
    },

    setDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, dateOverride: DateOverride }>,
    ) {
      const { dashboardId, panelId, dateOverride } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = dateOverride
    },

    clearDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string }>,
    ) {
      const { dashboardId, panelId } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = null
    },
  }),
})

export default graphSlice.reducer
